import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { WorkspaceShell } from "./WorkspaceShell";
import { ProductionStageSelect, ProofControl } from "@/pages/Admin";
import { ExternalLink } from "lucide-react";

/**
 * Production Workspace — Prime Supplier production board + Proof queue.
 */
export default function ProductionWorkspace() {
  const [exhibitors, setExhibitors] = useState([]);

  const load = () => api.get("/admin/exhibitors").then(({ data }) => setExhibitors(data.exhibitors || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const proofQueue = exhibitors.filter((e) => e.proof_status === "ready" || e.proof_status === "revision_requested");

  const tabs = [
    {
      value: "board", label: "Production Board",
      render: () => (
        <div className="card-flat overflow-x-auto" data-testid="prod-board">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="border-b border-zinc-200">
                <Th>Company</Th><Th>Logo</Th><Th>Stage</Th><Th>Proof</Th>
              </tr>
            </thead>
            <tbody>
              {exhibitors.map((e) => (
                <tr key={e.id} className="border-b border-zinc-100 last:border-0" data-testid={`prod-row-${e.id}`}>
                  <Td>
                    <Link to={`/admin/supplier/${e.id}`} className="font-medium hover:text-[#A39171] hover:underline underline-offset-4"
                          data-testid={`prod-open-project-${e.id}`}>
                      {e.company_name}
                    </Link>
                    <div className="text-xs text-zinc-500">{e.email}</div>
                  </Td>
                  <Td>
                    {e.logo_file_id ? (
                      <a href={`${BACKEND_URL}/api/files/${e.logo_file_id}`} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-2 text-xs underline underline-offset-4">
                        Logo <ExternalLink size={10} />
                      </a>
                    ) : (
                      <span className="text-xs text-zinc-400">Awaiting supplier</span>
                    )}
                  </Td>
                  <Td><ProductionStageSelect exhibitor={e} onSaved={(u) => setExhibitors((es) => es.map((x) => x.id === u.id ? u : x))} /></Td>
                  <Td><ProofControl exhibitor={e} onSaved={(u) => setExhibitors((es) => es.map((x) => x.id === u.id ? u : x))} /></Td>
                </tr>
              ))}
              {exhibitors.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-zinc-500">No Prime Suppliers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ),
    },
    {
      value: "proofs", label: `Proof Queue${proofQueue.length ? ` · ${proofQueue.length}` : ""}`,
      render: () => (
        <div data-testid="prod-proof-queue">
          {proofQueue.length === 0 ? (
            <div className="card-flat p-10 text-center text-sm text-zinc-500">
              No proofs awaiting supplier review or revision.
            </div>
          ) : (
            <div className="grid gap-3">
              {proofQueue.map((e) => (
                <div key={e.id} className="card-flat p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`prod-proof-${e.id}`}>
                  <div>
                    <Link to={`/admin/supplier/${e.id}`} className="font-medium hover:text-[#A39171] hover:underline underline-offset-4">
                      {e.company_name}
                    </Link>
                    <div className="text-xs text-zinc-500 mt-1">{e.primary_contact} · {e.email}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] mt-3 text-[#A39171]">
                      {e.proof_status === "ready" ? "Awaiting supplier approval" : "Revision requested"}
                    </div>
                    {e.proof_revision_comment && (
                      <div className="mt-2 text-xs text-zinc-700 border-l-2 border-amber-500 pl-3 max-w-lg">
                        &ldquo;{e.proof_revision_comment}&rdquo;
                      </div>
                    )}
                  </div>
                  <ProofControl exhibitor={e} onSaved={(u) => setExhibitors((es) => es.map((x) => x.id === u.id ? u : x))} />
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <WorkspaceShell
      title="Production"
      subtitle="Logos, artwork, proofs, approvals, print, pack, install — the physical build of every station."
      tabs={tabs}
      dataTestId="ws-title-production"
    />
  );
}

const Th = ({ children }) => <th className="text-xs uppercase tracking-[0.18em] text-zinc-500 px-4 py-3">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-4 align-top">{children}</td>;
